import React, {useEffect, useMemo, useRef, useState} from 'react';
import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query';
import {MagnifyingGlassIcon, XMarkIcon} from '@heroicons/react/24/outline';
import Button from '@/components/ui/Button';
import Card from '@/components/ui/Card';
import ConfirmModal from '@/components/ui/ConfirmModal';
import {SkeletonCard} from '@/components/ui/LoadingSkeleton';
import {cancelQueueItemMutation, operationsQueueQuery, recoverQueueItemMutation} from '@/features/operations/queries';
import type {OperationsError, QueueFilters, QueueItem, QueueSource, QueueState} from '@/features/operations/types';

const activeStates: QueueState[] = ['queued', 'running', 'cancel_requested'];
const historyStates: QueueState[] = ['completed', 'partial', 'failed', 'cancelled'];
const sources: QueueSource[] = ['scheduled_job', 'workspace_match_sync', 'external_player_import', 'player_analytics'];
const limit = 25;

const stateLabels: Record<QueueState, string> = {
  queued: 'Queued',
  running: 'Running',
  cancel_requested: 'Cancel requested',
  completed: 'Completed',
  partial: 'Partial',
  failed: 'Failed',
  cancelled: 'Cancelled',
};

const sourceLabels: Record<QueueSource, string> = {
  scheduled_job: 'Scheduled job',
  workspace_match_sync: 'Workspace match sync',
  external_player_import: 'External player import',
  player_analytics: 'Player analytics',
};

const phaseLabels: Record<string, string> = {
  discovering_matches: 'Discovering matches from Wyscout',
  importing_matches: 'Importing matches',
  cancelling: 'Cancelling',
};

const stateTone = (state: QueueState): string => {
  if (state === 'running') return 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300';
  if (state === 'queued' || state === 'cancel_requested') return 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300';
  if (state === 'completed') return 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300';
  if (state === 'partial') return 'bg-purple-100 text-purple-800 dark:bg-purple-900/40 dark:text-purple-300';
  return 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300';
};

const formatDate = (value?: string): string => value ? new Date(value).toLocaleString() : 'Not started';

const shortDuration = (ms: number): string => {
  if (!Number.isFinite(ms) || ms < 0) return '0s';
  const seconds = Math.round(ms / 1000);
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes}m`;
  return `${Math.round(minutes / 60)}h`;
};

const age = (item: QueueItem): string => shortDuration(Date.now() - new Date(item.createdAt).getTime());

const duration = (item: QueueItem): string => {
  const start = item.startedAt ?? item.createdAt;
  const end = item.completedAt ?? item.updatedAt;
  return end ? shortDuration(new Date(end).getTime() - new Date(start).getTime()) : 'In progress';
};

const progress = (item: QueueItem): string => {
  if (item.phase && (!item.total || item.phase === 'cancelling')) return phaseLabels[item.phase] ?? item.phase;
  if (!item.total) return '0%';
  return `${Math.round(((item.completed + item.failed + item.skipped) / item.total) * 100)}%`;
};

const initialJobKey = (): string => new URLSearchParams(window.location.search).get('jobKey') ?? '';

const safeSummary = (summary?: string): string => {
  if (!summary) return '';
  return summary
    .replace(/providerPayload\s*=\s*.+$/i, '')
    .replace(/rawPayload\s*=\s*.+$/i, '')
    .trim();
};

const canStop = (item: QueueItem): boolean => item.cancellable && (item.state === 'queued' || item.state === 'running');
const canRecover = (item: QueueItem): boolean => Boolean(item.recoverable) && (item.state === 'running' || item.state === 'cancel_requested');
const isTerminal = (item: QueueItem): boolean => historyStates.includes(item.state);
const queueItemKey = (item: Pick<QueueItem, 'source' | 'runId'>): string => `${item.source}:${item.runId}`;

const filterChipClass = (selected: boolean): string => [
  'inline-flex items-center rounded-full border px-3 py-1.5 text-sm font-medium transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-club-blue-500',
  selected
    ? 'border-club-blue-500 bg-club-blue-600 text-white shadow-sm dark:border-club-blue-400 dark:bg-club-blue-500'
    : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-950 dark:text-gray-200 dark:hover:bg-gray-800',
].join(' ');

const QueueTextFilter: React.FC<{
  label: string;
  value: string;
  placeholder: string;
  type?: 'search' | 'text';
  onChange: (value: string) => void;
}> = ({label, value, placeholder, type = 'text', onChange}) => {
  const inputId = React.useId();
  return (
    <div>
      <label htmlFor={inputId} className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-200">{label}</label>
      <div className="relative">
        {type === 'search' && <MagnifyingGlassIcon aria-hidden="true" className="pointer-events-none absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />}
        <input
          id={inputId}
          type={type}
          value={value}
          placeholder={placeholder}
          autoComplete="off"
          className={`min-h-11 w-full rounded-md border border-gray-300 bg-white py-2 pr-10 text-sm text-gray-900 shadow-sm placeholder:text-gray-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-club-blue-500 dark:border-gray-700 dark:bg-gray-950 dark:text-gray-100 dark:placeholder:text-gray-400 ${type === 'search' ? 'pl-10' : 'pl-3'}`}
          onChange={event => onChange(event.target.value)}
        />
        {value && (
          <button
            type="button"
            aria-label={`Clear ${label.toLowerCase()}`}
            className="absolute right-1 top-1/2 inline-flex min-h-9 min-w-9 -translate-y-1/2 items-center justify-center rounded-md text-gray-500 hover:bg-gray-100 hover:text-gray-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-club-blue-500 dark:hover:bg-gray-800 dark:hover:text-gray-100"
            onClick={() => onChange('')}
          >
            <XMarkIcon aria-hidden="true" className="h-5 w-5" />
          </button>
        )}
      </div>
    </div>
  );
};

const errorFrom = (error: unknown): OperationsError | undefined => (
  error as {response?: {data?: {error?: OperationsError}}}
).response?.data?.error;

const SafeQueueDetails: React.FC<{item: QueueItem}> = ({item}) => {
  const summary = safeSummary(item.summary);
  return (
    <details className="rounded-md border border-gray-200 bg-gray-50 p-3 text-sm dark:border-gray-800 dark:bg-gray-950">
      <summary className="cursor-pointer font-medium text-gray-800 dark:text-gray-200">View safe details</summary>
      <dl className="mt-3 grid gap-2 text-gray-700 dark:text-gray-300 sm:grid-cols-2">
        {summary && <div className="sm:col-span-2"><dt className="font-medium text-gray-500 dark:text-gray-400">Summary</dt><dd>{summary}</dd></div>}
        <div><dt className="font-medium text-gray-500 dark:text-gray-400">Run ID:</dt><dd className="break-all">{item.runId}</dd></div>
        <div><dt className="font-medium text-gray-500 dark:text-gray-400">Source:</dt><dd>{sourceLabels[item.source]}</dd></div>
        <div><dt className="font-medium text-gray-500 dark:text-gray-400">Type key:</dt><dd>{item.typeKey}</dd></div>
        <div><dt className="font-medium text-gray-500 dark:text-gray-400">Trigger:</dt><dd>{item.trigger || 'Unknown'}</dd></div>
        <div><dt className="font-medium text-gray-500 dark:text-gray-400">Workspace ID:</dt><dd className="break-all">{item.workspaceId || 'All workspaces'}</dd></div>
        <div><dt className="font-medium text-gray-500 dark:text-gray-400">Workspace:</dt><dd>{item.workspaceName || 'All workspaces'}</dd></div>
        <div><dt className="font-medium text-gray-500 dark:text-gray-400">Target ID:</dt><dd>{item.targetId || 'No target'}</dd></div>
        <div><dt className="font-medium text-gray-500 dark:text-gray-400">Target:</dt><dd>{item.targetLabel || 'No target'}</dd></div>
        <div><dt className="font-medium text-gray-500 dark:text-gray-400">Requested by:</dt><dd className="break-all">{item.requestedBy || 'System'}</dd></div>
        <div><dt className="font-medium text-gray-500 dark:text-gray-400">Cancel requested by:</dt><dd className="break-all">{item.cancelRequestedBy || 'Not requested'}</dd></div>
        <div><dt className="font-medium text-gray-500 dark:text-gray-400">Cancel requested at:</dt><dd>{formatDate(item.cancelRequestedAt)}</dd></div>
        <div><dt className="font-medium text-gray-500 dark:text-gray-400">Created</dt><dd>{formatDate(item.createdAt)}</dd></div>
        <div><dt className="font-medium text-gray-500 dark:text-gray-400">Updated</dt><dd>{formatDate(item.updatedAt)}</dd></div>
      </dl>
    </details>
  );
};

const QueueItemDetails: React.FC<{item: QueueItem}> = ({item}) => (
  <dl className="mt-3 grid gap-2 text-sm text-gray-700 dark:text-gray-300 sm:grid-cols-2">
    <div><dt className="font-medium text-gray-500 dark:text-gray-400">Workspace</dt><dd>{item.workspaceName || item.workspaceId || 'All workspaces'}</dd></div>
    <div><dt className="font-medium text-gray-500 dark:text-gray-400">Target</dt><dd>{item.targetLabel || item.targetId || 'No target'}</dd></div>
    <div><dt className="font-medium text-gray-500 dark:text-gray-400">Progress</dt><dd>{progress(item)} ({item.completed} done, {item.failed} failed, {item.skipped} skipped)</dd></div>
    <div><dt className="font-medium text-gray-500 dark:text-gray-400">Age:</dt><dd>{age(item)}</dd></div>
    <div><dt className="font-medium text-gray-500 dark:text-gray-400">Duration:</dt><dd>{duration(item)}</dd></div>
    <div><dt className="font-medium text-gray-500 dark:text-gray-400">Actor:</dt><dd>{item.requestedBy || item.cancelRequestedBy || 'System'}</dd></div>
  </dl>
);

const StopButton: React.FC<{item: QueueItem; onStop: (item: QueueItem, opener: HTMLButtonElement) => void}> = ({item, onStop}) => {
  if (!canStop(item)) return null;
  return (
    <Button
      type="button"
      variant="danger"
      size="sm"
      aria-label={`Stop ${item.label}`}
      onClick={event => onStop(item, event.currentTarget)}
    >
      Stop
    </Button>
  );
};

const RecoverButton: React.FC<{item: QueueItem; onRecover: (item: QueueItem) => void; loading: boolean}> = ({item, onRecover, loading}) => {
  if (!canRecover(item)) return null;
  return (
    <Button
      type="button"
      variant="outline"
      size="sm"
      aria-label={`Recover ${item.label}`}
      loading={loading}
      loadingLabel="Recovering"
      onClick={() => onRecover(item)}
    >
      Recover
    </Button>
  );
};

const StateBadge: React.FC<{state: QueueState}> = ({state}) => (
  <span className={`inline-flex rounded-full px-2.5 py-1 text-xs font-medium ${stateTone(state)}`}>{stateLabels[state]}</span>
);

const QueuePage: React.FC = () => {
  const queryClient = useQueryClient();
  const [selectedStates, setSelectedStates] = useState<QueueState[]>(activeStates);
  const [selectedSources, setSelectedSources] = useState<QueueSource[]>([]);
  const [jobKey, setJobKey] = useState(initialJobKey);
  const [workspaceId, setWorkspaceId] = useState('');
  const [search, setSearch] = useState('');
  const [cursor, setCursor] = useState<string | undefined>();
  const [lastUpdated, setLastUpdated] = useState<string>('');
  const [stopping, setStopping] = useState<QueueItem | null>(null);
  const [stopError, setStopError] = useState('');
  const [recoverError, setRecoverError] = useState('');
  const [cancelledRows, setCancelledRows] = useState<Record<string, QueueItem>>({});
  const [recoveredRows, setRecoveredRows] = useState<Record<string, QueueItem>>({});
  const stopOpener = useRef<HTMLButtonElement | null>(null);

  const filters = useMemo<QueueFilters>(() => ({
    state: selectedStates,
    ...(selectedSources.length ? {source: selectedSources} : {}),
    ...(jobKey.trim() ? {jobKey: jobKey.trim()} : {}),
    ...(workspaceId.trim() ? {workspaceId: workspaceId.trim()} : {}),
    ...(search.trim() ? {search: search.trim()} : {}),
    limit,
    ...(cursor ? {cursor} : {}),
  }), [cursor, jobKey, search, selectedSources, selectedStates, workspaceId]);

  const query = useQuery(operationsQueueQuery(filters));
  const cancelMutation = useMutation(cancelQueueItemMutation(queryClient));
  const recoverMutation = useMutation(recoverQueueItemMutation(queryClient));

  useEffect(() => {
    if (query.dataUpdatedAt) setLastUpdated(new Date(query.dataUpdatedAt).toLocaleTimeString());
  }, [query.dataUpdatedAt]);

  useEffect(() => {
    const onVisibilityChange = () => {
      if (!document.hidden) query.refetch();
    };
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => document.removeEventListener('visibilitychange', onVisibilityChange);
  }, [query]);

  const resetCursor = () => setCursor(undefined);
  const setStateFilter = (state: QueueState, checked: boolean) => {
    resetCursor();
    setSelectedStates(current => checked ? [...current, state] : current.filter(value => value !== state));
  };
  const setSourceFilter = (source: QueueSource, checked: boolean) => {
    resetCursor();
    setSelectedSources(current => checked ? [...current, source] : current.filter(value => value !== source));
  };
  const setHistory = (checked: boolean) => {
    resetCursor();
    setSelectedStates(checked ? [...activeStates, ...historyStates] : activeStates);
  };

  const setStatePreset = (states: QueueState[]) => {
    resetCursor();
    setSelectedStates(states);
  };

  const hideCompleted = () => {
    resetCursor();
    setSelectedStates(current => current.filter(state => state !== 'completed'));
  };

  const clearAllFilters = () => {
    setJobKey('');
    setWorkspaceId('');
    setSearch('');
    setSelectedSources([]);
    setSelectedStates(activeStates);
    resetCursor();
  };

  const restoreStopFocus = () => {
    const opener = stopOpener.current;
    queueMicrotask(() => opener?.focus());
    window.setTimeout(() => opener?.focus(), 0);
  };

  const openStopConfirm = (item: QueueItem, opener: HTMLButtonElement) => {
    setStopError('');
    stopOpener.current = opener;
    setStopping(item);
  };

  const closeStopConfirm = () => {
    if (cancelMutation.isPending) return;
    setStopping(null);
    setStopError('');
    restoreStopFocus();
  };

  const confirmStop = async () => {
    if (!stopping || cancelMutation.isPending) return;
    setStopError('');
    try {
      const cancelled = await cancelMutation.mutateAsync({source: stopping.source, runId: stopping.runId});
      setCancelledRows(current => ({...current, [queueItemKey(cancelled)]: cancelled}));
      setStopping(null);
      restoreStopFocus();
    } catch (error) {
      setStopError(errorFrom(error)?.message ?? 'Operation could not be stopped.');
    }
  };

  const recoverItem = async (item: QueueItem) => {
    if (recoverMutation.isPending) return;
    setRecoverError('');
    try {
      const recovered = await recoverMutation.mutateAsync({source: item.source, runId: item.runId});
      setRecoveredRows(current => ({...current, [queueItemKey(recovered)]: recovered}));
    } catch (error) {
      setRecoverError(errorFrom(error)?.message ?? 'Operation could not be recovered.');
    }
  };

  if (query.isLoading) return <SkeletonCard label="Loading queue" />;

  const items: QueueItem[] = (query.data?.items ?? []).map((item: QueueItem) => {
    const recovered = recoveredRows[queueItemKey(item)];
    if (recovered) return recovered;
    const cancelled = cancelledRows[queueItemKey(item)];
    if (!cancelled || isTerminal(item)) return item;
    return cancelled;
  });
  const hasHistory = historyStates.every(state => selectedStates.includes(state));

  return (
    <div className="space-y-6 text-gray-900 dark:text-gray-100">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold">Queue</h1>
          <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">Operations queue across scheduled jobs, workers, imports, and analytics.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <a className="inline-flex items-center rounded-md border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-700 dark:text-gray-200 dark:hover:bg-gray-800" href="/admin/scheduling">Scheduling</a>
          <Button type="button" variant="outline" onClick={() => query.refetch()} loading={query.isFetching} loadingLabel="Refreshing queue">Refresh queue</Button>
        </div>
      </div>

      <Card>
        <div className="grid gap-4 lg:grid-cols-4">
          <QueueTextFilter label="Job key" value={jobKey} placeholder="e.g. materialized_views" onChange={value => { resetCursor(); setJobKey(value); }} />
          <QueueTextFilter label="Workspace ID" value={workspaceId} placeholder="Paste workspace UUID" onChange={value => { resetCursor(); setWorkspaceId(value); }} />
          <QueueTextFilter label="Search queue" type="search" value={search} placeholder="Job, workspace, player or error" onChange={value => { resetCursor(); setSearch(value); }} />
          <div className="flex flex-wrap items-end gap-2 pb-0.5">
            <label className="inline-flex min-h-11 items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-300"><input type="checkbox" checked={hasHistory} onChange={event => setHistory(event.target.checked)} />Include recent 30-day history</label>
            <Button type="button" variant="outline" className="min-h-11" onClick={clearAllFilters}>Clear all filters</Button>
          </div>
        </div>
        <fieldset className="mt-4">
          <legend className="text-sm font-semibold text-gray-700 dark:text-gray-300">States</legend>
          <div className="mt-2 flex flex-wrap gap-2">
            <button type="button" className={filterChipClass(activeStates.every(state => selectedStates.includes(state)) && selectedStates.length === activeStates.length)} aria-pressed={activeStates.every(state => selectedStates.includes(state)) && selectedStates.length === activeStates.length} onClick={() => setStatePreset(activeStates)}>Active</button>
            <button type="button" className={filterChipClass(historyStates.every(state => selectedStates.includes(state)) && selectedStates.length === historyStates.length)} aria-pressed={historyStates.every(state => selectedStates.includes(state)) && selectedStates.length === historyStates.length} onClick={() => setStatePreset(historyStates)}>History</button>
            <button type="button" className={filterChipClass(!selectedStates.includes('completed'))} aria-pressed={!selectedStates.includes('completed')} onClick={hideCompleted}>Hide completed</button>
            <button type="button" className={filterChipClass(selectedStates.every(state => activeStates.includes(state)))} aria-pressed={selectedStates.every(state => activeStates.includes(state))} onClick={() => setStatePreset(activeStates)}>Clear history</button>
          </div>
          <div className="mt-3 flex flex-wrap gap-2">
            {[...activeStates, ...historyStates].map(state => (
              <button key={state} type="button" className={filterChipClass(selectedStates.includes(state))} aria-pressed={selectedStates.includes(state)} onClick={() => setStateFilter(state, !selectedStates.includes(state))}>{stateLabels[state]}</button>
            ))}
          </div>
        </fieldset>
        <fieldset className="mt-4">
          <legend className="text-sm font-semibold text-gray-700 dark:text-gray-300">Sources</legend>
          <div className="mt-2 flex flex-wrap gap-2">
            {sources.map(source => (
              <button key={source} type="button" className={filterChipClass(selectedSources.includes(source))} aria-pressed={selectedSources.includes(source)} onClick={() => setSourceFilter(source, !selectedSources.includes(source))}>{sourceLabels[source]}</button>
            ))}
          </div>
        </fieldset>
      </Card>

      {query.isError && <div role="alert" className="rounded-md bg-red-50 p-3 text-sm text-red-700 dark:bg-red-900/20 dark:text-red-300">Queue could not be loaded.</div>}
      {recoverError && <div role="alert" className="rounded-md bg-red-50 p-3 text-sm text-red-700 dark:bg-red-900/20 dark:text-red-300">{recoverError}</div>}

      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <p className="text-sm text-gray-600 dark:text-gray-400">Last updated: {lastUpdated || 'Not yet'}</p>
        <div className="flex gap-2">
          <Button type="button" variant="outline" disabled={!cursor} onClick={() => setCursor(undefined)}>First page</Button>
          <Button type="button" disabled={!query.data?.nextCursor} onClick={() => setCursor(query.data?.nextCursor)}>Next page</Button>
        </div>
      </div>

      <div className="hidden xl:block">
        <Card padding="none">
          <table aria-label="Operations queue" className="min-w-full divide-y divide-gray-200 dark:divide-gray-800">
            <thead className="bg-gray-50 dark:bg-gray-950"><tr>{['Job', 'State', 'Source', 'Progress', 'Age', 'Duration', 'Actor', 'Actions', 'Details'].map(header => <th key={header} scope="col" className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">{header}</th>)}</tr></thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-800">
              {items.map(item => <tr key={`${item.source}:${item.runId}`}><td className="px-4 py-3 font-medium">{item.label}<div className="text-xs text-gray-500">{item.typeKey}</div></td><td className="px-4 py-3"><StateBadge state={item.state} /></td><td className="px-4 py-3">{sourceLabels[item.source]}</td><td className="px-4 py-3">{progress(item)}</td><td className="px-4 py-3">{age(item)}</td><td className="px-4 py-3">{duration(item)}</td><td className="px-4 py-3">{item.requestedBy || item.cancelRequestedBy || 'System'}</td><td className="px-4 py-3"><div className="flex flex-wrap gap-2"><StopButton item={item} onStop={openStopConfirm} /><RecoverButton item={item} onRecover={recoverItem} loading={recoverMutation.isPending && recoverMutation.variables?.runId === item.runId} /></div></td><td className="px-4 py-3"><SafeQueueDetails item={item} /></td></tr>)}
            </tbody>
          </table>
        </Card>
      </div>

      <div className="space-y-4 xl:hidden">
        {items.map(item => (
          <article key={`${item.source}:${item.runId}`} aria-label={`${item.label} queue item`} className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm dark:border-gray-800 dark:bg-gray-900">
            <div className="flex items-start justify-between gap-3">
              <div><h2 className="font-semibold">{item.label}</h2><p className="text-sm text-gray-500 dark:text-gray-400">{sourceLabels[item.source]}</p></div>
              <StateBadge state={item.state} />
            </div>
            <QueueItemDetails item={item} />
            <div className="mt-3 flex flex-wrap gap-2"><StopButton item={item} onStop={openStopConfirm} /><RecoverButton item={item} onRecover={recoverItem} loading={recoverMutation.isPending && recoverMutation.variables?.runId === item.runId} /></div>
            <div className="mt-3"><SafeQueueDetails item={item} /></div>
          </article>
        ))}
      </div>

      {items.length === 0 && <Card><p className="text-sm text-gray-600 dark:text-gray-400">No queue items match these filters.</p></Card>}

      <ConfirmModal
        isOpen={Boolean(stopping)}
        title="Stop queued operation?"
        message={stopping ? (
          <div className="space-y-3">
            <p>Request cancellation for {sourceLabels[stopping.source]} operation {stopping.label}.</p>
            <dl className="grid gap-2 text-sm sm:grid-cols-2">
              <div><dt className="font-medium text-gray-500 dark:text-gray-400">Type</dt><dd>{stopping.typeKey}</dd></div>
              <div><dt className="font-medium text-gray-500 dark:text-gray-400">Target</dt><dd>{stopping.targetLabel || stopping.targetId || 'No target'}</dd></div>
            </dl>
            {stopError && <div role="alert" className="rounded-md bg-red-50 p-3 text-sm text-red-700 dark:bg-red-900/20 dark:text-red-300">{stopError}</div>}
          </div>
        ) : null}
        confirmLabel="Stop operation"
        cancelLabel="Keep running"
        tone="danger"
        loading={cancelMutation.isPending}
        onClose={closeStopConfirm}
        onConfirm={() => { void confirmStop(); }}
      />
    </div>
  );
};

export default QueuePage;
